/**
 * StockItemRepository — the ingredient master.
 *
 * Ingredients are raw materials a recipe consumes: rice, chicken, oil. They
 * live in StockTable and are served by the POS sync API so a till and the
 * dashboard see the same list.
 *
 *   GET    /stock/items     list
 *   POST   /stock/items     create
 *   PUT    /stock/items     merge-patch
 *   DELETE /stock/items     soft delete (deactivate)
 *
 * On-hand is NOT written here. It is derived from the ledger and refreshed by
 * the cloud; this repository only carries config. To move an ingredient's
 * quantity, append an event through StockEventRepository or post an intake.
 *
 * See docs/feature-stock-intake-and-suppliers.md §5.1.
 */
import {posApiRequest} from './posApi';

/** The six units the ingredient master has always offered. */
export const METRIC_OPTIONS = [
  {value: 'kg', label: 'kg (Kilogram)', step: 0.01},
  {value: 'l', label: 'l (Litre)', step: 0.01},
  {value: 'g', label: 'g (Gram)', step: 1},
  {value: 'ml', label: 'ml (Millilitre)', step: 1},
  {value: 'm', label: 'm (Meter)', step: 0.01},
  {value: 'Item', label: 'Item (per Item)', step: 1},
];

/** How finely a quantity in this unit may be entered. */
export function stepFor(metricValue?: string | null): number {
  return METRIC_OPTIONS.find((m) => m.value === metricValue)?.step ?? 0.01;
}

export interface StockItem {
  stockId: string;
  itemName: string;
  metric: string;
  metricValue: string;
  parLevel?: number | null;
  isActive?: boolean;
  defaultSupplierId?: string | null;
  /** Derived — written by the cloud, never by this app. */
  stockOnHand?: number;
  stockBaselineAt?: string | null;
  stockUpdatedAt?: string | null;
  lastUnitCost?: number | null;
  lastPurchasedAt?: string | null;
  createdTime?: number;
  updatedTime?: number;
}

export interface StockItemInput {
  itemName: string;
  metricValue: string;
  parLevel?: number | null;
  defaultSupplierId?: string | null;
}

const StockItemRepository = {
  async list(shopId: string, includeInactive = false): Promise<StockItem[]> {
    const q = `merchantId=${encodeURIComponent(shopId)}${includeInactive ? '&includeInactive=true' : ''}`;
    const res = await posApiRequest(`/stock/items?${q}`);
    return res?.items ?? [];
  },

  async create(shopId: string, item: StockItemInput): Promise<StockItem> {
    const res = await posApiRequest('/stock/items', {
      method: 'POST',
      body: JSON.stringify({merchantId: shopId, item}),
    });
    return res?.item;
  },

  /**
   * Merge-patch. Fields left out keep their value.
   *
   * Changing `metricValue` once the ingredient has any stock history is
   * refused by the cloud with a 409 — every stored quantity is in the old
   * unit and nothing records which, so 2 kg would silently become 2 g.
   */
  async update(shopId: string, item: Partial<StockItem> & {stockId: string}): Promise<StockItem> {
    const res = await posApiRequest('/stock/items', {
      method: 'PUT',
      body: JSON.stringify({merchantId: shopId, item}),
    });
    return res?.item;
  },

  /**
   * Deactivate. Never a hard delete: a recipe or a posted intake references
   * an ingredient forever. The cloud refuses with a 409 while any active
   * recipe still names it.
   */
  async deactivate(shopId: string, stockId: string): Promise<StockItem> {
    const res = await posApiRequest(
      `/stock/items?merchantId=${encodeURIComponent(shopId)}&stockId=${encodeURIComponent(stockId)}`,
      {method: 'DELETE'},
    );
    return res?.item;
  },
};

export default StockItemRepository;
